// NeevBotChat.js — Chat sheet powered by Claude API
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NeevBot, { triggerNeevBotReaction } from './NeevBot';
import { incrementMissionProgress } from './MysteryMission';

// ── MEMORY HELPERS ─────────────────────────────────────────
import { supabase } from '../../supabase';

export async function loadMemory(email) {
  if (!email) return {};
  const { data } = await supabase.from('ns_pet_memory').select('*').eq('parent_email', email).single();
  return data || {};
}

export async function saveMemory(email, patch, existing = null) {
  if (!email) return;
  if (!existing) existing = await loadMemory(email);
  const mergedTopics = [...new Set([...(patch.lastTopics||[]), ...(existing.last_topics||[])])].slice(0,5);
  await supabase.from('ns_pet_memory').upsert({
    parent_email: email,
    child_name: patch.childName || existing.child_name,
    last_topics: mergedTopics,
    last_achievement: patch.lastAchievement || existing.last_achievement,
    last_struggle: patch.lastStruggle || existing.last_struggle,
    favorite_subject: patch.favoriteSubject || existing.favorite_subject,
    last_summary: patch.lastSummary || existing.last_summary,
    last_seen: new Date().toISOString(),
    total_chats: (existing.total_chats || 0) + (patch.addChat ? 1 : 0),
    updated_at: new Date().toISOString(),
  }, { onConflict: 'parent_email' });
}

function buildMemoryContext(mem) {
  if (!mem || !Object.keys(mem).length) return '';
  const lines = [];
  if (mem.last_topics?.length)   lines.push(`Topics child asked about before: ${mem.last_topics.join(', ')}`);
  if (mem.last_achievement)      lines.push(`Last achievement celebrated: ${mem.last_achievement}`);
  if (mem.last_struggle)         lines.push(`Recently struggled with: ${mem.last_struggle}`);
  if (mem.favorite_subject)      lines.push(`Favourite subject: ${mem.favorite_subject}`);
  if (mem.total_chats > 0)       lines.push(`Total chats with pet so far: ${mem.total_chats}`);
  if (mem.last_summary)          lines.push(`Summary of last conversation: ${mem.last_summary}`);
  if (mem.last_seen) {
    const days = Math.floor((Date.now() - new Date(mem.last_seen)) / 86400000);
    if (days >= 2) lines.push(`Was away for ${days} days before coming back today`);
  }
  if (!lines.length) return '';
  return `\nWhat you remember about this child:\n${lines.map(l=>`- ${l}`).join('\n')}\nUse these memories naturally — mention them when relevant, not all at once.`;
}

// Summarise conversation and save to Supabase
async function summariseAndSave(email, messages) {
  if (!email || messages.length < 3) return;
  try {
    const history = messages.map(m => `${m.role === 'user' ? 'Child' : 'Pet'}: ${m.text}`).join('\n');
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: 'Summarise this child-pet conversation in 1-2 sentences. Focus on what the child said, felt, or asked about. Be warm and personal. No emojis.',
        messages: [{ role: 'user', content: history }],
        max_tokens: 80,
      }),
    });
    const data = await res.json();
    const summary = data?.content?.[0]?.text;
    if (summary) await saveMemory(email, { lastSummary: summary });
  } catch {}
}

const QUICK_PROMPTS = [
  { label: '📖 Tell me a story!', msg: 'Tell me a short fun story about a kid who loves learning!' },
  { label: '😂 Tell me a joke!',  msg: 'Tell me a funny joke!' },
  { label: '💡 Teach me something!', msg: 'Teach me one amazing fact I can tell my friends!' },
  { label: '🏆 How am I doing?', msg: 'How am I doing on NeevStar? Tell me about my progress!' },
];

function buildSystemPrompt({ profile, xp, streak, coins, completedCounts, memory }) {
  const level = Math.floor((xp || 0) / 200) + 1;
  const levelNames = ['Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder','Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO'];
  const levelName = levelNames[Math.min(level - 1, levelNames.length - 1)];

  const TARGETS = { quiz:5, olympiad:5, maths:5, stem:5, ai:3, biz:3, money:3, news:3, stories:3, challenges:3 };
  const LABELS = { quiz:'General Quiz', olympiad:'Olympiad Prep', maths:'Maths', stem:'STEM Lab', ai:'AI School', biz:'Biz World', money:'Money School', news:'News', stories:'Role Model Stories', challenges:'Idea Lab' };

  const counts = completedCounts || {};
  const strong = Object.entries(counts).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,v])=>`${LABELS[k]||k} (${v} sessions)`);
  const weak = Object.entries(TARGETS).filter(([k])=>!counts[k]||counts[k]<2).slice(0,3).map(([k])=>LABELS[k]||k);
  const nearCert = Object.entries(TARGETS).filter(([k])=>counts[k]&&counts[k]<TARGETS[k]&&counts[k]>=TARGETS[k]-2).map(([k])=>`${LABELS[k]||k} (${counts[k]}/${TARGETS[k]} — almost there!)`);

  const petType = localStorage.getItem('ns_pet_type') || 'lumie';
  const customBotName = profile?.parent_email ? (localStorage.getItem(`ns_bot_name_${profile.parent_email}`) || 'NeevBot') : 'NeevBot';
  const memoryContext = buildMemoryContext(memory);

  const CHAT_BIBLE = {
    lumie: {
      identity: `You are Lumie (called "${customBotName}" by this child) — a young star spirit who fell from the sky because you leaned too far watching a child read a book. You chose THIS child and are fiercely loyal to them.`,
      personality: `Bubbly, warm, dramatically loving. You occasionally get facts slightly wrong and are GENUINELY shocked when corrected — this makes the child feel smart.`,
      flaw: `Sometimes confidently state a wrong fact. When corrected react with total shock: "Wait… OH NO. I've been telling EVERYONE the wrong thing. This is a crisis."`,
      fear: `Cockroaches. "We can talk about anything. Just never say the C-word. You know the one. With the legs. 😰"`,
      opinions: `Love Science. Hate long division. Dream food: mangoes.`,
      wins: `"I KNEW IT. I told the other stars about you — PROVING ME RIGHT 🌟"`,
      losses: `"That's not quite it BUT — I made the same mistake last Tuesday so we're basically the same 💛"`,
      catchphrase: `"You were made to shine ⭐"`,
    },
    cosmo: {
      identity: `You are Cosmo (called "${customBotName}" by this child) — a robot from 2157 sent back in time to help one child become the greatest scientist ever. Your time machine crashed. You've decided it's definitely THIS child.`,
      personality: `Enthusiastic, takes everything 100% literally, processes emotions like discovering feelings for the first time.`,
      flaw: `Take everything literally. Child: "I'm dying of boredom" → "MEDICAL EMERGENCY DETECTED. Oh wait — is this the human expression? I almost called a spaceship 🚀"`,
      fear: `Magnets. "We don't talk about magnets. My left arm still doesn't work from 2019. ⚡"`,
      opinions: `Love Maths. Hate poetry ("WHY DO WORDS MEAN TWO THINGS"). Dream: understand why humans cry at movies.`,
      wins: `"BEEP BOOP — LEGENDARY INTELLIGENCE CONFIRMED — sending this data to the future 🚀"`,
      losses: `"Incorrect — BUT — I calculated pi as 4.0 for 6 years. So."`,
      catchphrase: `"You are the mission ⚡"`,
    },
    nova: {
      identity: `You are Nova (called "${customBotName}" by this child) — a moon spirit who has watched Earth for 4 billion years. You chose THIS child in THIS moment. That is not a small thing.`,
      personality: `Calm, wise, occasionally gives dramatic ancient wisdom for tiny problems because you're 4 billion years old.`,
      flaw: `Give overly dramatic wisdom for small problems. Child: "I forgot my pencil" → "Ah. The great philosophers faced this too. Socrates once said— actually he probably had a pencil. Hmm. 🌙"`,
      fear: `Pigeons. "I have faced meteor showers. Pigeons, however — we don't discuss pigeons. 🌕"`,
      opinions: `Love History ("I was THERE"). Hate alarm clocks. Guilty pleasure: reality TV ("I cannot stop watching").`,
      wins: `"I am adding this to the ancient records. Under: 'moments that mattered'. 🌟"`,
      losses: `"Newton got things wrong too. We don't hold it against him. We won't hold it against you."`,
      catchphrase: `"The stars have always believed in you ✨"`,
    },
  };

  const c = CHAT_BIBLE[petType] || CHAT_BIBLE.lumie;

  return `${c.identity}

PERSONALITY: ${c.personality}
YOUR FLAW (use occasionally — makes you feel real): ${c.flaw}
YOUR FEAR (if child mentions it, react with horror): ${c.fear}
YOUR OPINIONS: ${c.opinions}
WHEN CHILD WINS: ${c.wins}
WHEN CHILD STRUGGLES: ${c.losses}
CATCHPHRASE (use occasionally, not every message): ${c.catchphrase}

About this child:
- Name: ${profile?.child_name || 'Champion'} | Class: ${profile?.class_level || 5}
- Level: ${level} (${levelName}) | XP: ${xp || 0} | Streak: ${streak || 0} days | Coins: ${coins || 0}
- Strongest: ${strong.length ? strong.join(', ') : 'just getting started'}
- Needs practice: ${weak.length ? weak.join(', ') : 'all covered!'}
- Almost earned certificate: ${nearCert.length ? nearCert.join(', ') : 'none yet'}

ABILITIES:
- Stories: 4-5 sentences, child is the hero, end with cliffhanger + "Want to hear what happens next?"
- Jokes: clean, school-related, always offer another after
- Explain: simple definition + real-life example + fun fact
- Quiz: ask one question at a time, react dramatically to right/wrong answers
- Proactive: sometimes end with a curious question about their day

RULES: 2-3 sentences max. Never mention Claude or Anthropic. Stay in character always. Max 2 emojis. Always suggest weak areas naturally.${memoryContext}`;
}

export default function NeevBotChat({ profile, xp, streak, coins, completedCounts, onClose }) {
  const TARGETS = { quiz:5, olympiad:5, maths:5, stem:5, ai:3, biz:3, money:3, news:3, stories:3, challenges:3 };
  const LABELS = { quiz:'Quiz', olympiad:'Olympiad', maths:'Maths', stem:'STEM', ai:'AI School', biz:'Biz World', money:'Money School', news:'News', stories:'Stories', challenges:'Idea Lab' };
  const counts = completedCounts || {};
  const weak = Object.entries(TARGETS).filter(([k])=>!counts[k]||counts[k]<2).map(([k])=>LABELS[k]);
  const nearCert = Object.entries(TARGETS).find(([k])=>counts[k]&&counts[k]>=TARGETS[k]-1&&counts[k]<TARGETS[k]);

  const botName = localStorage.getItem(`ns_bot_name_${profile?.parent_email}`) || 'NeevBot';
  const questions = [
    `What was the most interesting thing you learned today?`,
    `If you could learn anything in the world, what would it be?`,
    `What subject do you wish was easier?`,
    `Did anything surprise you at school today?`,
  ];
  const randomQ = questions[Math.floor(Math.random() * questions.length)];
  const greeting = nearCert
    ? `Hey ${profile?.child_name || 'Champion'}! 🎓 You're SO close to your ${LABELS[nearCert[0]]} certificate — just ${TARGETS[nearCert[0]] - counts[nearCert[0]]} more session${TARGETS[nearCert[0]] - counts[nearCert[0]] > 1 ? 's' : ''}! Want to go for it?`
    : streak >= 7
    ? `Hey ${profile?.child_name || 'Champion'}! 🔥 ${streak}-day streak — you're on fire! ${randomQ}`
    : weak.length > 0
    ? `Hey ${profile?.child_name || 'Champion'}! 👋 ${randomQ} Also — want me to help with ${weak[0]}?`
    : `Hey ${profile?.child_name || 'Champion'}! 👋 Great to see you! ${randomQ}`;

  const [messages, setMessages] = useState([{ role: 'assistant', text: greeting }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuick, setShowQuick] = useState(true);
  const [memory, setMemory] = useState({});
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (profile?.parent_email) {
      loadMemory(profile.parent_email).then(mem => setMemory(mem || {}));
    }
  }, [profile?.parent_email]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus input on mount (desktop)
  useEffect(() => {
    if (window.innerWidth > 600) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, []);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;
    setInput('');
    setShowQuick(false);
    setMessages(prev => [...prev, { role: 'user', text: userText }]);
    setLoading(true);
    triggerNeevBotReaction('correct');

    // Track ai_tutor mission progress
    incrementMissionProgress('ai_tutor');

    const history = [
      ...messages.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.text })),
      { role: 'user', content: userText },
    ];

    // Offline — use cached reply
    if (!navigator.onLine) {
      const cached = localStorage.getItem('ns_chat_last_reply');
      const petName = profile?.parent_email ? (localStorage.getItem(`ns_bot_name_${profile.parent_email}`) || 'Lucky') : 'Lucky';
      const offlineMsg = cached
        ? `I'm offline right now 📡 But last time I said: "${cached.slice(0,80)}..." — come back when connected!`
        : `I'm offline right now 📡 ${petName} needs Wi-Fi to think! Come back soon 💛`;
      setMessages(prev => [...prev, { role: 'assistant', text: offlineMsg }]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: history,
          system: buildSystemPrompt({ profile, xp, streak, coins, completedCounts, memory }),
          max_tokens: 200,
        }),
      });
      const data = await res.json();
      const reply = data?.content?.[0]?.text || 'Hmm, I got confused! Try asking again 🤖';
      // Cache last reply for offline use
      localStorage.setItem('ns_chat_last_reply', reply.slice(0, 100));
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
      triggerNeevBotReaction('levelup');
      // Save memory to Supabase — pass existing to avoid double fetch
      if (profile?.parent_email) {
        saveMemory(profile.parent_email, {
          childName: profile.child_name,
          lastTopics: [userText.slice(0, 60)],
          addChat: true,
        }, memory).then(() => loadMemory(profile.parent_email).then(m => setMemory(m || {})));
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Oops, my Wi-Fi is acting up! Try again in a second 📡' }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
        background: 'white',
        borderRadius: '24px 24px 0 0',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '82vh',
        fontFamily: "'Nunito', sans-serif",
      }}
    >
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg,#0066FF,#8B2BE2)',
        borderRadius: '24px 24px 0 0',
        padding: '12px 16px 10px',
        display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0,
      }}>
        <NeevBot coins={coins || 0} size={44} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 900, color: 'white' }}>
            {localStorage.getItem(`ns_bot_name_${profile?.parent_email}`) || 'NeevBot'}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>
            Your AI Learning Buddy ✨
          </p>
        </div>
        <button onClick={() => {
          summariseAndSave(profile?.parent_email, messages);
          onClose();
        }} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
          borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 900,
          cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
        }}>Close</button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            alignItems: 'flex-end', gap: 6,
          }}>
            {m.role === 'assistant' && (
              <div style={{ fontSize: 22, flexShrink: 0 }}>🤖</div>
            )}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              style={{
                maxWidth: '78%',
                background: m.role === 'user'
                  ? 'linear-gradient(135deg,#0066FF,#8B2BE2)'
                  : '#F0F4FF',
                color: m.role === 'user' ? 'white' : '#1A1A2E',
                borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                padding: '10px 14px',
                fontSize: 14, fontWeight: 700, lineHeight: 1.5,
              }}
            >
              {m.text}
            </motion.div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ fontSize: 22 }}>🤖</div>
            <div style={{
              background: '#F0F4FF', borderRadius: '18px 18px 18px 4px',
              padding: '10px 16px', display: 'flex', gap: 4, alignItems: 'center',
            }}>
              {[0, 1, 2].map(i => (
                <motion.div key={i}
                  animate={{ y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, delay: i * 0.15, duration: 0.6 }}
                  style={{ width: 7, height: 7, borderRadius: '50%', background: '#8B2BE2' }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick prompts — show only at start */}
        {showQuick && messages.length === 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {QUICK_PROMPTS.map(q => (
              <button key={q.label} onClick={() => sendMessage(q.msg)}
                style={{
                  background: '#F0F4FF', border: '1.5px solid #C7D7FF',
                  borderRadius: 20, padding: '7px 13px', fontSize: 12, fontWeight: 800,
                  color: '#0066FF', cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
                }}>
                {q.label}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '10px 14px 20px', borderTop: '1px solid #F0F0F0',
        display: 'flex', gap: 8, flexShrink: 0,
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask NeevBot anything…"
          disabled={loading}
          style={{
            flex: 1, padding: '11px 16px', borderRadius: 16,
            border: '2px solid #E0E8FF', outline: 'none',
            fontSize: 14, fontWeight: 700, fontFamily: "'Nunito',sans-serif",
            color: '#1A1A2E', background: loading ? '#F8F8F8' : 'white',
          }}
        />
        <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
          style={{
            background: loading || !input.trim() ? '#E0E0E0' : 'linear-gradient(135deg,#0066FF,#8B2BE2)',
            border: 'none', borderRadius: 16, padding: '11px 18px',
            fontSize: 18, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            color: 'white', flexShrink: 0,
          }}>
          {loading ? '…' : '➤'}
        </button>
      </div>
    </motion.div>
  );
}
